import React, { useRef, useEffect, useState, useCallback } from "react";
import MultipleImages from "../UC/MultipleImages";
import CatalogInformation from "../UC/CatalogInformation";
import EvidenceInformation from "../UC/EvidenceInformation";
import { useParams, useNavigate } from 'react-router-dom';
import '../../assets/css/details.scss';
import api from '../../utils/api';

import ZoomModal from '../Pages/ZoomModal';

import "../../config";

export default function Details({ container, record: initialRecord, onClose, arrAccounts, recordScore }) {
    const modalRef = useRef(null);
    const { id, bucket } = useParams(); // Get the ID and optional bucket from URL parameters
    const navigate = useNavigate(); // Add navigation hook
    const [record, setRecord] = useState(initialRecord);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch record details if not provided via props (URL-based navigation)
    useEffect(() => {
        // Only fetch when navigating by URL (id present) and no initial record
        if (initialRecord || !id) return;

        let cancelled = false;
        const fetchRecordDetails = async () => {
            setIsLoading(true);
            setError(null);

            try {
                let fetchedRecord = null;
                const searchPayload = { PageNumber: 1, PageSize: 1, IDs: [id] };
                const extractRecord = (res) =>
                    res.data?.Records?.length > 0 ? res.data.Records[0] : null;

                if (bucket === 'catalog' || bucket === 'evidence') {
                    // Bucket known — single call
                    const response = await api.post(
                        `${global.config.api.domain}/fwiq-api/api/${bucket}/search`,
                        searchPayload
                    );
                    fetchedRecord = extractRecord(response);
                } else {
                    // Bucket unknown — fire both in parallel, prefer catalog
                    const [catalogResult, evidenceResult] = await Promise.allSettled([
                        api.post(`${global.config.api.domain}/fwiq-api/api/catalog/search`, searchPayload),
                        api.post(`${global.config.api.domain}/fwiq-api/api/evidence/search`, searchPayload),
                    ]);

                    const catalogRecord = catalogResult.status === 'fulfilled'
                        ? extractRecord(catalogResult.value) : null;
                    const evidenceRecord = evidenceResult.status === 'fulfilled'
                        ? extractRecord(evidenceResult.value) : null;

                    fetchedRecord = catalogRecord || evidenceRecord;
                }

                if (cancelled) return;

                if (fetchedRecord) {
                    setRecord(fetchedRecord);
                } else {
                    setError('Record not found');
                }
            } catch (error) {
                if (!cancelled) {
                    console.error('Error fetching record details:', error);
                    setError('Failed to load record details');
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        fetchRecordDetails();
        return () => { cancelled = true; };
    }, [id, bucket, initialRecord]);

    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
    const [isZoomOpen, setIsZoomOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Handle closing the modal
    const handleClose = useCallback(() => {
        if (onClose) {
            onClose();
        } else if (id) {
            // If we're viewing from URL, navigate back to gallery
            // Use bucket parameter if available, otherwise default to gallery
            if (bucket && (bucket === 'catalog' || bucket === 'evidence')) {
                navigate(`/gallery`); // Gallery component will handle the bucket type internally
            } else {
                navigate('/gallery');
            }
        }
    }, [onClose, id, bucket, navigate]);

    // Handle keyboard events for URL-based viewing
    useEffect(() => {
        if (id) {
            const handleKeyDown = (e) => {
                if (e.key === "Escape") {
                    handleClose();
                }
            };

            document.addEventListener("keydown", handleKeyDown);
            return () => document.removeEventListener("keydown", handleKeyDown);
        }
    }, [id, handleClose]);

    // Handle window resize to determine mobile/desktop view
    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        // Initial check
        handleResize();

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Determine if the category includes "impression" (case insensitive)
    const isImpression = record.Category_ID === undefined || record?.Category_ID !== global.config.Defines.CategoryID.NewShoe;

    // Handle image selection change from MultipleImages component
    const handleImageChange = (index) => {
        setCurrentImageIndex(index);
    };

    // Handle opening the zoom modal
    const handleZoomImage = () => {
        setIsZoomOpen(true);
    };

    const sortImagesByStandardized = (images) => {
        const arrStandardized = images.filter(img => img.IsStandardize === 1);

        let arrResult = [];

        arrStandardized.forEach(stdImg => {
            arrResult.push(stdImg);
            let originalImg = images.find(img => stdImg.Original_IDValue === img.IDValue);
            if (originalImg) {
                arrResult.push(originalImg);
            }
        });

        // add remaining images that are neither standardized nor originals
        images.forEach(img => {
            if (!arrResult.find(rImg => rImg.IDValue === img.IDValue)) {
                arrResult.push(img);
            }
        });

        return arrResult; // Remove any undefined entries
    }

    return (
        <>
            <div className={`modal details`} ref={modalRef}>
                <div className="modal-header">
                    <h3>Details</h3>
                    <div className="close" onClick={handleClose}>
                        <i className="fa fa-times" aria-hidden="true"></i>
                    </div>
                </div>
                <div className={`modal-body ${isMobileView ? 'single-column' : 'two-column'}`}>
                    {isLoading ? (
                        <div className="loading-container" style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '200px',
                            flexDirection: 'column'
                        }}>
                            <i className="fa fa-spinner fa-spin" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
                            <p>Loading details...</p>
                        </div>
                    ) : record ? (
                        <>
                            <div className="modal-images">
                                <MultipleImages
                                    images={sortImagesByStandardized(record.Images)} // Standardized images first
                                    handleZoomImage={handleZoomImage}
                                    defaultIndex={currentImageIndex}
                                    onImageChange={handleImageChange}
                                    imageScores={record.Scores}
                                />
                            </div>
                            <div className="modal-data">
                                {isImpression ? (
                                    <EvidenceInformation
                                        info={record}
                                        arrAccounts={arrAccounts}
                                        layout={isMobileView ? 'single-column' : 'two-column'}
                                    />
                                ) : (
                                    <CatalogInformation
                                        info={record}
                                        arrAccounts={arrAccounts}
                                        layout={isMobileView ? 'single-column' : 'two-column'}
                                    />
                                )}
                            </div>
                        </>
                    ) : error ? (
                        <div className="error-container" style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '200px',
                            flexDirection: 'column'
                        }}>
                            <i className="fa fa-exclamation-triangle" style={{ fontSize: '2rem', marginBottom: '1rem', color: '#dc3545' }}></i>
                            <p>{error}</p>
                        </div>
                    ) : !record ? (
                        <div className="error-container" style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '200px',
                            flexDirection: 'column'
                        }}>
                            <i className="fa fa-exclamation-triangle" style={{ fontSize: '2rem', marginBottom: '1rem', color: '#dc3545' }}></i>
                            <p>No record found or failed to load details.</p>
                        </div>
                    ) : null}
                </div>
            </div>

            {record && (
                <ZoomModal
                    images={record.Images}
                    isOpen={isZoomOpen}
                    onClose={() => setIsZoomOpen(false)}
                    initialIndex={currentImageIndex}
                    isAnalyze={false}
                />
            )}
        </>
    )
}